import { Component, ElementRef, inject, Input, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { Popover, PopoverModule } from 'primeng/popover';
import { TooltipModule } from 'primeng/tooltip';

// Composants
import { CONVERSATION_QUERY_PARAM, SELECTED_QUERY_PARAM } from '../abstract/base.component';

// Services
import { MessageServiceBroker } from 'src/app/@core/services/message.service';

/**
 * Bouton de partage d'une vue de liste (#961).
 *
 * Le geste existait déjà — l'adresse d'une liste porte le dossier, l'écran et les filtres —
 * mais rien ne l'indiquait : il fallait savoir que l'adresse était signifiante, penser à
 * aller la chercher dans la barre du navigateur, et vérifier qu'on l'avait copiée en entier.
 * Ce bouton rend le geste évident sans rien changer au mécanisme.
 *
 * ⚠️ **Il ne partage que les critères, pas la mise en forme.** Ce qui voyage réellement,
 * c'est le dossier, l'écran et le `$filter`. Le tri, la page et les colonnes sont des
 * préférences personnelles, mémorisées par utilisateur dans le `TableState` : l'état
 * enregistré du destinataire est réappliqué à l'arrivée et réécrit l'adresse
 * (`TableBaseComponent._applyTableStateToTable`). Ce n'est pas un manque à combler ici —
 * c'est la règle décrite au manuel utilisateur.
 *
 * Le composant est volontairement muet sur la liste qui le porte : il lit l'adresse
 * courante, rien d'autre. C'est ce qui permet d'équiper n'importe quelle liste par une
 * ligne de gabarit, sans développement dédié.
 */
@Component({
    selector: 'app-table-share-button',
    imports: [
        CommonModule,
        ButtonModule,
        InputTextModule,
        PopoverModule,
        TooltipModule,
    ],
    templateUrl: './table-share-button.component.html'
})
export class TableShareButtonComponent {

    /*----------------------------------------------------------------------------------------
     * Gestion de la vue
     ----------------------------------------------------------------------------------------*/

    @ViewChild('fallbackPopover') public fallbackPopover!: Popover;
    @ViewChild('fallbackInput') public fallbackInput?: ElementRef<HTMLInputElement>;

    /*----------------------------------------------------------------------------------------
     * Données
     ----------------------------------------------------------------------------------------*/

    /** Infobulle du bouton, surchargeable par la liste qui le porte. */
    @Input() public tooltip: string = 'Copier le lien de cette vue, filtres compris';

    /** Lien de la vue courante, alimenté au clic et affiché en repli de copie. */
    public shareUrl: string = '';

    private _messageService = inject(MessageServiceBroker);
    private _router = inject(Router);

    /*----------------------------------------------------------------------------------------
     * Méthodes publiques
     ----------------------------------------------------------------------------------------*/

    public onClickLink(event: MouseEvent): void {
        this._selectLink(event.target as HTMLInputElement);
    }

    public onClickShare(event: MouseEvent): void {
        this._copyViewLink(event.currentTarget as HTMLElement);
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes privées
     ----------------------------------------------------------------------------------------*/

    /**
     * Adresse absolue de la vue courante.
     *
     * `selected` et `conversation` en sont retirés : ils n'appartiennent pas à la vue de
     * liste mais à la fenêtre ouverte par-dessus (#995). Les partager enverrait le
     * destinataire sur une ligne précise alors qu'on lui envoie une sélection.
     */
    private _buildViewUrl(): string {
        const urlTree = this._router.parseUrl(this._router.url);
        const queryParams = { ...urlTree.queryParams };

        delete queryParams[SELECTED_QUERY_PARAM];
        delete queryParams[CONVERSATION_QUERY_PARAM];
        urlTree.queryParams = queryParams;

        return window.location.origin + this._router.serializeUrl(urlTree);
    }

    /**
     * `navigator.clipboard` est absent hors contexte sécurisé et sa promesse est rejetée
     * quand le navigateur refuse le droit d'écriture. Dans les deux cas le lien reste
     * récupérable à la main, plutôt que perdu sans recours.
     */
    private _copyViewLink(target: HTMLElement): void {
        this.shareUrl = this._buildViewUrl();

        if (!navigator.clipboard) {
            this._showManualFallback(target);
            return;
        }

        navigator.clipboard.writeText(this.shareUrl).then(
            () => this._messageService.sendMessage({
                severity: 'success',
                summary: 'Lien copié',
                detail: 'Le lien de cette vue a été copié dans le presse-papier.',
                life: 2000
            }),
            () => this._showManualFallback(target)
        );
    }

    private _selectLink(input: HTMLInputElement | null): void {
        input?.select();
    }

    /**
     * L'élément d'ancrage est passé plutôt que l'évènement : le repli s'ouvre depuis le
     * rejet d'une promesse, où `currentTarget` a déjà été remis à zéro par le navigateur.
     */
    private _showManualFallback(target: HTMLElement): void {
        this.fallbackPopover.show(null, target);
        setTimeout(() => this._selectLink(this.fallbackInput?.nativeElement ?? null), 1);
    }
}
